import { IHostFacade, importUMD, InterceptionMatch, IShell, IShellCore, IShellCoreExtensionConstructor, IShellExtensionConstructor, logger, MFConfig, mountInShadowDOM, ShellConfiguration, ShellConfigurationOverrides, ShellInterceptions } from "@mf-tools/cli-common";
import "@mf-tools/design-system-components/css";
import { MfThemeController } from "@mf-tools/design-system-runtime";
import * as GlobalState from "@mf-tools/global-state";
import { CSSManager } from "./css-manager";
import { MFSlotElement } from "./mf-slot";
import { PathMatcher } from "./path-matcher";


const log = logger.browser('shell-sdk');

window.require = (moduleName) => {
  log.info('Loading module: ', moduleName);
  // Map module names to their corresponding global variables
  const moduleMap = {
    'react': window.React,
    'react-dom': window.ReactDOM,
    'history': window.H,
    'react-router': window.ReactRouter,
    'react-router-dom': window.ReactRouterDOM,
    '@mf-tools/design-system-runtime': window.MfDesignSystemRuntime,
    'MfGlobalState': GlobalState,
    'zustand': window.zustand,
    'zustand/vanilla': window.zustand,
    'axios': window.axios,
    'react-query': window.ReactQuery,
    // Add more mappings for other modules as needed

  };

  // Check if the module is mapped to a global variable
  if (moduleMap.hasOwnProperty(moduleName)) {
    return moduleMap[moduleName];
  }

  // Throw an error if the module is not found
  throw new Error(`Module '${moduleName}' not found`);
}


class Shell implements IShell {
  private cssManager: CSSManager;
  private configuration: ShellConfiguration & ShellConfigurationOverrides;
  private originalConfiguration: ShellConfiguration;
  private originalInterceptions: ShellInterceptions;
  private shellCore: IShellCore;
  private pathMatcher: PathMatcher;

  private hostFacade: any = null;
  private hostFacadeReadyResolver?: () => void;
  private hostFacadeReady: Promise<void>;


  constructor() {
    log.info('Shell constructor');

    // Create promise that waits for Angular to register facade
    this.hostFacadeReady = new Promise((resolve) => {
      this.hostFacadeReadyResolver = resolve;
    });
  }

  /**
   * Resolves the CDN base URL for runtime scripts by name.
   * Critical: Returns a valid string, never undefined. Throws explicit error on misconfig/misuse.
   * Prioritization:
   *   1. Explicit configuration.runtimeUrl (overrides all)
   *   2. Safe fallback to window.location.origin + /__cdn__ (only if running in browser context)
   *   3. Hardcoded vendor CDN map (whitelist only: never constructs dynamic URLs!)
   * 
   * Throws error on invalid name or if resolution is impossible (e.g. SSR without window).
   */
  private getRuntimeScriptCDN = (name: string): string => {
    // Use runtimeUrl override from configuration if present and non-empty
    if (
      this.configuration &&
      typeof this.configuration.runtimeUrl === 'string' &&
      this.configuration.runtimeUrl.trim() !== ''
    ) {
      return this.configuration.runtimeUrl;
    }

    // Allowed external vendor CDN prefixes (whitelist only)
    const defaultExternalCDNPrefixMap: Record<string, string> = Object.freeze({
      'react': 'https://unpkg.com',
      'history': 'https://unpkg.com',
      'reactDom': 'https://unpkg.com',
      'reactRouter': 'https://cdnjs.cloudflare.com',
      'reactRouterDOM': 'https://cdnjs.cloudflare.com',
      'axios': 'https://cdn.jsdelivr.net',
      'reactQuery': 'https://unpkg.com',
    });

    // Try fallback to local CDN if available in browser environment
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      // Bulletproof: check that window.location.origin is well-formed
      // (browser guarantees this property; if not present, we are likely in SSR/node and must fail)
      return `${window.location.origin}/__cdn__`;
    }

    // Fallback to vendor CDN mapping. Only if explicit name present in map.
    if (defaultExternalCDNPrefixMap.hasOwnProperty(name)) {
      return defaultExternalCDNPrefixMap[name];
    }

    // Fail: No way to resolve CDN for this name, either config or known vendor
    throw new Error(
      `[Shell:getRuntimeScriptCDN] Failed to resolve CDN for module name '${name}'. ` +
      `No runtimeUrl override, window context, or known vendor prefix for this module.`
    );
  }

  private async loadRuntime(): Promise<any> {
    log.info('🚀 Starting runtime loading...');
    const startTime = performance.now();

    // Determine if we should load development or production builds
    // Priority: localStorage override > configuration override > build-time default
    const localOverride = localStorage.getItem('shell.runtimeMode');
    const configOverride = (this.configuration as any).runtimeMode;
    const isDevRuntime = localOverride
      ? (localOverride === 'development' || localOverride === 'stage')
      : configOverride
        ? (configOverride === 'development' || configOverride === 'stage')
        : __MF_IS_DEV_RUNTIME__;

    const reactSuffix = isDevRuntime ? 'development.js' : 'production.min.js';
    log.info(`📦 Runtime mode: ${isDevRuntime ? 'DEVELOPMENT' : 'PRODUCTION'} (React: ${reactSuffix})`);
    if (localOverride) {
      log.info(`⚡ Using localStorage override: ${localOverride}`);
    } else if (configOverride) {
      log.info(`⚙️  Using configuration override: ${configOverride}`);
    } else {
      log.info(`🔧 Using build-time default: ${__MF_RUNTIME_MODE__}`);
    }

    // Load fonts and set CSS variables
    log.info('📝 Loading fonts and CSS...');
    const cssStart = performance.now();
    await Promise.all([
      this.cssManager.loadFonts(),
      this.cssManager.getBundledDesignSystemCSS()
    ]);
    const cssTime = performance.now() - cssStart;
    log.info(`✅ Fonts and CSS loaded in ${cssTime.toFixed(2)}ms`);

    // Load libraries in parallel while respecting dependencies
    log.info('📦 Loading JavaScript libraries in parallel...');
    const libsStart = performance.now();

    // Phase 1: Load React and History in parallel (independent)
    log.info('🔄 Phase 1: Loading React + History...');
    await Promise.all([
      this.loadScriptTag(`${this.getRuntimeScriptCDN('react')}/react@18.3.1/umd/react.${reactSuffix}`, 'react'),
      this.loadScriptTag(`${this.getRuntimeScriptCDN('history')}/history@5.3.0/umd/history.production.min.js`, 'history'),
      this.loadScriptTag(`${this.getRuntimeScriptCDN('reactDom')}/react-dom@18.3.1/umd/react-dom.${reactSuffix}`, 'reactDom'),
      this.loadScriptTag(`${this.getRuntimeScriptCDN('axios')}/npm/axios@1.7.9/dist/axios.min.js`, 'axios'),
      this.loadScriptTag(`${this.getRuntimeScriptCDN('reactQuery')}/@tanstack/react-query@4.41.0/build/umd/index.production.js`, 'reactQuery')
    ]);

    log.info('React loaded:', !!window.React);
    log.info('React version:', window.React?.version);
    log.info('ReactDOM loaded:', !!window.ReactDOM);

    // History exports to window.HistoryLibrary, but we need it as window.H
    window.H = window.HistoryLibrary;

    // Phase 2: Load ReactDOM (needs React to be ready)
    log.info('🔄 Phase 2: Loading ReactDOM...');

    // Phase 3: Load Router libraries in parallel (both need React+ReactDOM)
    log.info('🔄 Phase 3: Loading Router libraries...');
    await Promise.all([
      this.loadScriptTag(`${this.getRuntimeScriptCDN('reactRouter')}/ajax/libs/react-router/6.3.0/react-router.production.min.js`, 'reactRouter'),
      this.loadScriptTag(`${this.getRuntimeScriptCDN('reactRouterDOM')}/ajax/libs/react-router-dom/6.3.0/react-router-dom.production.min.js`, 'reactRouterDOM')
    ]);

    const libsTime = performance.now() - libsStart;
    log.info(`✅ All libraries loaded in ${libsTime.toFixed(2)}ms`);

    // Use bundled design system runtime (theme controller)
    window.MfDesignSystemRuntime = { MfThemeController };

    // Debug CSS variables after everything loads
    log.info('🎨 CSS Variables Debug:');
    log.info('Document root variables:', {
      primary: getComputedStyle(document.documentElement).getPropertyValue('--mf-primary'),
      textPrimary: getComputedStyle(document.documentElement).getPropertyValue('--mf-text-primary'),
      fontFamily: getComputedStyle(document.documentElement).getPropertyValue('--mf-font-family')
    });

    const totalTime = performance.now() - startTime;
    log.info(`🎉 Runtime loading completed in ${totalTime.toFixed(2)}ms (css: ${cssTime.toFixed(2)}ms, libs: ${libsTime.toFixed(2)}ms)`);
  }

  private loadScriptTag(url: string, globalName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = performance.now();
      const script = document.createElement('script');
      script.src = url;
      script.async = false; // Important: Keep execution order
      script.crossOrigin = 'anonymous';
      script.defer = true;

      script.onload = () => {
        const loadTime = performance.now() - startTime;
        log.info(`⚡ ${globalName} loaded in ${loadTime.toFixed(2)}ms`);
        resolve();
      };

      script.onerror = () => {
        const loadTime = performance.now() - startTime;
        const error = new Error(`Failed to load ${globalName} from ${url} (after ${loadTime.toFixed(2)}ms)`);
        log.error(error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  private async withOverrides(configuration: ShellConfiguration): Promise<ShellConfiguration & ShellConfigurationOverrides> {
    log.info('With overrides', configuration);

    const _configuration: ShellConfiguration & ShellConfigurationOverrides = JSON.parse(JSON.stringify(configuration));

    // SECURITY: Only apply localStorage overrides if management panel is enabled in base configuration
    // This prevents unauthorized users from injecting malicious MF URLs via localStorage
    if (!configuration.enableManagementPanel) {
      log.info('Management panel disabled - ignoring localStorage overrides for security');
      return _configuration;
    }

    const _configurationOverrides: ShellConfigurationOverrides = JSON.parse(localStorage.getItem('shell.overrides') || '{}');

    if (_configurationOverrides.cdn) {
      _configuration.cdn = _configurationOverrides.cdn;
    }

    if (_configurationOverrides.runtimeUrl) {
      _configuration.runtimeUrl = _configurationOverrides.runtimeUrl;
    }

    if (_configurationOverrides.enableManagementPanel) {
      _configuration.enableManagementPanel = _configurationOverrides.enableManagementPanel;
    }

    if (_configurationOverrides.mfConfig) {

      for (const domain in _configurationOverrides.mfConfig) {
        // Ensure domain exists in configuration
        if (!_configuration.mfConfig[domain]) {
          _configuration.mfConfig[domain] = {};
        }

        for (const name in _configurationOverrides.mfConfig[domain]) {
          // Merge override with existing config, or create new entry if it doesn't exist
          _configuration.mfConfig[domain][name] = {
            ..._configuration.mfConfig[domain][name],  // May be undefined for local-only MFs
            ..._configurationOverrides.mfConfig[domain][name]
          };
        }
      }
    }

    if (_configurationOverrides.interceptions) {
      _configuration.interceptions = {
        ..._configuration.interceptions,
        ..._configurationOverrides.interceptions
      };
    }

    log.info('Configuration with overrides', _configuration);

    return _configuration;
  }

  private async loadShellCore(shellCoreExtensions: IShellCoreExtensionConstructor[]): Promise<void> {
    try {
      log.info('Loading shell-core MF...');

      shellCoreExtensions?.forEach((ShellCoreExtensionCtor) => {
        const shellCoreExtension = new ShellCoreExtensionCtor(this.shellCore);
        shellCoreExtension.preInit?.();
      });

      const shellCoreConfig: MFConfig = this.configuration.mfConfig['shell']['core'];

      const version = shellCoreConfig.version;

      let url = '';
      if (shellCoreConfig.override && !shellCoreConfig.override.isDisabled) {

        if (shellCoreConfig.override?.version) {
          log.info('Shell-core loaded from override version', shellCoreConfig.override.version);

          url = `${this.configuration.cdn}/shell/core/${shellCoreConfig.override.version}/module-shell-core.js`;

        } else {
          url = shellCoreConfig.override.entry;
          log.info('Shell-core loaded from override', url);

          if (shellCoreConfig.override.isDev) {
            const { origin } = new URL(url);
            await importUMD(`${origin}/reactRefreshSetup.js`)
          }
        }

      } else {
        url = `${this.configuration.cdn}/shell/core/${version}/module-shell-core.js`;
      }

      const shellCoreMF = await importUMD(url) as any;
      const shellCoreModule = new shellCoreMF.ShellCoreModule();
      log.info('Shell-core MF loaded successfully', shellCoreModule);

      const shellCoreMFElement = document.createElement('div');
      shellCoreMFElement.id = 'shell-core-mf';
      document.body.appendChild(shellCoreMFElement);

      mountInShadowDOM({
        containerElement: shellCoreMFElement,
        reactComponent: shellCoreMF.default,
        props: {},
        cssManager: this.cssManager,
        React: window.React,
        ReactDOM: window.ReactDOM,
        ReactRouterDOM: window.ReactRouterDOM,
      });

      log.info('Initializing Shell-core MF...');
      await shellCoreModule.init(this.configuration, this.cssManager, shellCoreMFElement, this.originalConfiguration);
      log.info('Shell-core MF initialized successfully');

      this.shellCore = shellCoreModule;

      shellCoreExtensions?.forEach((ShellCoreExtensionCtor) => {
        const shellCoreExtension = new ShellCoreExtensionCtor(this.shellCore);
        shellCoreExtension.postInit?.();
      });

    } catch (error) {
      log.error('Failed to load shell-core MF:', error);
    }
  }

  async loadMF(domain: string, name: string, container: HTMLElement, props: Record<string, any>): Promise<void> {
    try {
      await this.shellCore.loadMF(domain, name, container, props);
    } catch (error) {
      log.error('Error loading micro frontends:', error);
    }
  }

  async unloadMF(container: HTMLElement): Promise<void> {
    // TODO: Implement module unloading
    log.info('Unloading module:', container);
    this.shellCore.unloadMF(container);
    log.info('Module unloaded:', container);
  }

  /**
   * Get interception configuration for a given path
   *
   * Supports parameterized routes:
   * - /frameworks/:frameworkId/overview
   * - /frameworks/folders/:folderId
   *
   * @param fullpath - The full path to match (may include query params)
   * @returns Interception match with extracted params, or null if no match
   *
   * @example
   * // Pattern: /frameworks/:frameworkId/overview
   * // Path: /frameworks/123/overview
   * // Returns: { domain: 'app', name: 'framework-detail', props: { frameworkId: '123' }, params: { frameworkId: '123' } }
   */
  getInterceptionConfiguration(fullpath: string): InterceptionMatch | null {
    log.info('getInterceptionConfiguration', fullpath);

    if (!this.pathMatcher) {
      log.warn('PathMatcher not initialized, falling back to exact match');
      const config = this.originalInterceptions[fullpath];
      if (config) {
        return {
          domain: config.domain,
          name: config.name,
          props: config.props || {},
          params: {}
        };
      }
      return null;
    }

    return this.pathMatcher.match(fullpath);
  }

  /**
 * Called by Angular to register the facade
 */
  registerShellFacade(facade: IHostFacade): void {
    this.hostFacade = facade;
    log.info('Host facade registered');
    this.hostFacadeReadyResolver?.();
  }

  async init(configuration: ShellConfiguration, shellExtensions: IShellExtensionConstructor[], shellCoreExtensions: IShellCoreExtensionConstructor[]): Promise<void> {
    log.info('Shell init', configuration, shellExtensions, shellCoreExtensions);

    shellExtensions?.forEach((ShellExtensionCtor) => {
      const shellExtension = new ShellExtensionCtor(this);
      shellExtension.preInit?.();
    });

    // Store original configuration before merging with overrides
    this.originalConfiguration = JSON.parse(JSON.stringify(configuration));
    this.configuration = await this.withOverrides(configuration);
    this.originalInterceptions = JSON.parse(JSON.stringify(configuration?.interceptions || {}));

    // Initialize PathMatcher for route interception with parameterized routes
    // Use merged configuration (includes overrides) not original
    this.pathMatcher = new PathMatcher(this.configuration.interceptions);
    log.info('PathMatcher initialized with interceptions', this.configuration.interceptions);

    // Set the public path for the shell chunks built with mf-ui-cli index.template.ts
    window.__SHELL_PUBLIC_PATH__ = configuration.cdn ? new URL(configuration.cdn).href : '';

    document.addEventListener('mf-slot:connected', (e) => {
      const detail = (e as CustomEvent).detail;
      log.info('✅ Connected:', detail.id);
      log.info('Domain:', detail.domain);
      log.info('Name:', detail.name);
      log.info('Props:', detail.props);
      log.info('Element:', detail.element);

      // Use the data to load MF
      if (detail.domain && detail.name) {
        this.loadMF(detail.domain, detail.name, detail.element, detail.props);
      }
    });

    document.addEventListener('mf-slot:mount', (e) => {
      log.info('🎯 Mounted:', (e as any).detail.id, (e as any).detail.shadowRoot);
    });

    document.addEventListener('mf-slot:disconnected', (e) => {
      log.info('❌ Disconnected:', (e as any).detail.id);
    });

    log.info('Configuration with overrides', this.configuration);

    const mfSdkScript = document.getElementById('mf-sdk-script') as HTMLScriptElement;
    let mfSdkCssUrl = mfSdkScript.src.replace('.js', '.css');
    this.cssManager = new CSSManager({ cssUrl: mfSdkCssUrl });

    await this.loadRuntime();

    // Load shell-core MF automatically
    await this.loadShellCore(shellCoreExtensions);

    log.info('Waiting for host facade to be registered...');
    const facadeReadyStart = performance.now();
    await this.hostFacadeReady;
    const facadeReadyTime = performance.now() - facadeReadyStart;
    log.info(`Host facade ready in ${facadeReadyTime.toFixed(2)}ms`);
    log.info('Host facade ready, init complete');

    // Register the custom element
    if (typeof window !== 'undefined' && !customElements.get('mf-slot')) {
      customElements.define('mf-slot', MFSlotElement);
      log.info('[MFSlot] Custom element registered');
    }

    if (shellExtensions && shellExtensions.length > 0) {
      await Promise.all(
        shellExtensions.map(async (ShellExtensionCtor) => {
          const shellExtension = new ShellExtensionCtor(this);
          if (typeof shellExtension.postInit === 'function') {
            return shellExtension.postInit?.();
          }
        })
      );
    }

    // await this.shellCore.initAnecdotesApp(this.hostFacade);
  }
}

(async () => {
  const shell = new Shell();

  // Expose shell to window for debugging and Angular integration
  (window as any).__mfShell = shell;

  // Create a promise that resolves when shell is ready
  let resolveShellReady: () => void;
  (window as any).__shellReady = new Promise<void>((resolve) => {
    resolveShellReady = resolve;
  });

  // Initialize shell - this will automatically load shell-core MF with floating button
  await shell.init(window.__SHELL_CONFIG__, window.__SHELL_EXTENSIONS__, window.__SHELL_CORE_EXTENSIONS__);

  // Shell-core MF is now loaded and will handle MF management through its UI
  log.info('Shell initialized with shell-core MF management UI');

  // Signal that shell is ready
  resolveShellReady!();

})();